import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create the auth context
const AuthContext = createContext(null);

// Storage keys
const TOKEN_KEY = 'losspay_token';
const POLICIES_KEY = 'losspay_policies';
const REMEMBER_ME_KEY = 'losspay_remember_me';

// Auth provider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Validate token on mount and when token changes
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    console.log('[AuthContext] validateToken called, token exists:', !!token);
    
    if (!token) {
      console.log('[AuthContext] No token found, setting not authenticated');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      console.log('[AuthContext] Validating token with API...');
      const response = await fetch(`${API_URL}/api/auth/validate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();
      console.log('[AuthContext] Token validation response:', data);

      if (data.valid) {
        console.log('[AuthContext] Token valid, setting authenticated');
        setUser({
          email: data.email,
          policyNumber: data.policy_number,
          policies: data.policies || []
        });
        setIsAuthenticated(true);
      } else {
        // Token is invalid, clear it
        console.log('[AuthContext] Token invalid, clearing storage');
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(POLICIES_KEY);
        localStorage.removeItem(REMEMBER_ME_KEY);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('[AuthContext] Token validation error:', error);
      // On network error, trust the token exists and let individual API calls validate
      // This prevents redirect loops when the validation endpoint is slow/unavailable
      const policies = JSON.parse(localStorage.getItem(POLICIES_KEY) || '[]');
      if (token && policies.length > 0) {
        console.log('[AuthContext] Network error but token exists, allowing access');
        setUser({
          email: 'unknown',
          policyNumber: policies[0],
          policies: policies
        });
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      console.log('[AuthContext] Setting loading=false');
      setLoading(false);
    }
  }, []);

  // Check token on mount
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Login function - called after successful OTP verification
  const login = useCallback((token, policies, email, policyNumber, rememberMe = false) => {
    console.log('[AuthContext] login called with:', { email, policyNumber, policiesCount: policies?.length, rememberMe });
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(POLICIES_KEY, JSON.stringify(policies));
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
    
    setUser({
      email,
      policyNumber,
      policies
    });
    setIsAuthenticated(true);
    console.log('[AuthContext] login complete, isAuthenticated set to true');
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    
    // Call logout API if we have a token
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
    }

    // Clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(POLICIES_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    
    // Reset state
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Get the auth token
  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  // Check if remember me was enabled
  const isRememberMeEnabled = useCallback(() => {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  }, []);

  // Check if user has access to a specific policy
  const hasAccessToPolicy = useCallback((policyNumber) => {
    if (!user || !user.policies) return false;
    return user.policies.includes(policyNumber.toUpperCase());
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    getToken,
    hasAccessToPolicy,
    validateToken,
    isRememberMeEnabled
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
